import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import { useCallback, useState } from 'react';
import InvoiceHttpService from '../../../services/http/invoice-http';
import LoadingModalBody from '../../LoadingModalBody';
import InvoiceValidationForm from './InvoiceValidationForm';
import handleResponseError from '../../../utils/handleResponseError';
import InvoiceItemsUnavailable from './InvoiceItemsUnavailable';

interface InvoiceValidationModalProps {
    show: boolean;
    onHide: () => void;
    invoiceId: number;
}

export default function InvoiceValidationModal({
    show,
    onHide,
    invoiceId,
}: InvoiceValidationModalProps) {
    const [minDeliveryDate, setMinDeliveryDate] = useState<Date>(new Date());
    const [datesWithQuantity, setDatesWithQuantity] = useState<
        Array<{ date: Date; quantity: number }>
    >([]);
    const [unavailableItems, setUnavailableItems] = useState<string[]>([]);

    const commissionMutation = useMutation({
        mutationFn: () => InvoiceHttpService.updateCommission(invoiceId),
        onError: () => {
            onHide();
            toast.error(
                'Ocorreu um erro ao atualizar o valor líquido do serviço',
            );
        },
        onSuccess: async (data) => {
            if (data.data?.updated) {
                toast.warn('Valor líquido do serviço atualizado');
            }
        },
    });

    const waitingCommissionUpdate =
        commissionMutation.isLoading ||
        (commissionMutation.isIdle && !unavailableItems.length);

    const recalculateMutation = useMutation({
        mutationFn: () => InvoiceHttpService.recalculate(invoiceId.toString()),
        onError: (error: any) => {
            onHide();
            handleResponseError(
                error,
                'Ocorreu um erro ao atualizar disponibilidade dos itens do pedido.',
            );
        },
        onSuccess: async ({ data }) => {
            toast.info(
                data?.updated
                    ? 'A disponibilidade dos itens foi atualizada'
                    : 'Não ocorreram atualizações de disponibilidade',
            );
            if (data.unavailableItems.length > 0) {
                setUnavailableItems(data.unavailableItems);
            } else {
                setUnavailableItems([]);
                commissionMutation.mutate();
            }
        },
    });

    const waitingRecalculate =
        recalculateMutation.isLoading || recalculateMutation.isIdle;

    const canValidateQuery = useQuery({
        queryKey: ['can-validate', invoiceId],
        queryFn: async () => {
            const response = await InvoiceHttpService.canValidate(invoiceId);
            return response.data;
        },
        onError: (error: any) => {
            onHide();

            return handleResponseError(
                error,
                'Erro ao verificar se o pedido pode ser validado',
            );
        },
        onSuccess: async (data) => {
            setMinDeliveryDate(new Date(data?.minDeliveryDate));
            setDatesWithQuantity(
                data?.deliveryDateSeparationCapacity.map((value: any) => {
                    return {
                        date: new Date(value.date),
                        quantity: value.quantity,
                    };
                }),
            );
        },
        enabled:
            !waitingRecalculate &&
            !waitingCommissionUpdate &&
            !unavailableItems?.length,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        refetchOnReconnect: false,
        retry: false,
    });

    const showProgressBar =
        waitingRecalculate ||
        waitingCommissionUpdate ||
        canValidateQuery.isFetching;

    const getLoadingMessage = useCallback(() => {
        if (waitingRecalculate) {
            return 'Atualizando a disponibilidade dos itens do pedido...';
        }
        if (waitingCommissionUpdate) {
            return 'Atualizando valor líquido do serviço...';
        }

        return 'Verificando se o pedido pode ser validado...';
    }, [waitingCommissionUpdate, waitingRecalculate]);

    const loadingMessage = getLoadingMessage();

    const performUpdates = useCallback(() => {
        recalculateMutation.mutate();
    }, [recalculateMutation]);

    return (
        <Modal
            aria-labelledby="invoice-validation-modal"
            show={show}
            onHide={onHide}
            size={!unavailableItems.length ? 'lg' : undefined}
            centered
            backdrop={!showProgressBar || 'static'}
            onShow={performUpdates}
        >
            {showProgressBar && <LoadingModalBody message={loadingMessage} />}

            {!showProgressBar && unavailableItems.length > 0 && (
                <InvoiceItemsUnavailable
                    invoiceCodeItems={unavailableItems}
                    invoiceId={invoiceId}
                    onHide={onHide}
                />
            )}

            {!showProgressBar && !unavailableItems.length && (
                <InvoiceValidationForm
                    invoice={canValidateQuery?.data?.invoice}
                    onSubmitError={onHide}
                    minDeliveryDate={minDeliveryDate}
                    dateWithQuantity={datesWithQuantity}
                />
            )}
        </Modal>
    );
}
